// ============================================
// Saved export files (`sit.export` v1)
// ============================================
//
// The persistent form of a full device export, built for the fleet-review
// workflow: run a session → export → save file, repeat across ~100 units,
// then open the files weeks later (no device needed) and get EXACTLY the
// view the export produced on the day.
//
// Fidelity strategy: store the RAW record bytes (base64) plus the export
// banner — the same shape the Electron main-process reader returns — and
// re-run buildExportResult() on open. The review view is decoded by the
// same code path as a live export, CRC validation included, and files stay
// readable when the decoder learns new record versions. A full 1022-record
// store is ~35 MB of JSON; a typical two-session unit is a few MB.

import { t } from "i18n";
import { wearableFileShortName } from "./centrifugeFile";
import {
  groupBySession,
  summarizeExport,
  featBandDb,
  FEAT_AXES,
  FEAT_BAND_HZ,
} from "./wearableExport";
import { sessionWindow, coverage } from "./sessionTimeline";
import { recordKey, emptyReview } from "./videoReview";

export const EXPORT_FILE_FORMAT = "sit.export";
export const EXPORT_FILE_VERSION = 3;
// v1 files carry raw + wearable only. They still open: everything v2 and v3
// add is context ABOUT the dump, never a substitute for it.
export const EXPORT_FILE_MIN_VERSION = 1;

const iso = (ms) =>
  ms == null || Number.isNaN(ms) ? null : new Date(ms).toISOString();

/**
 * Decoded digest of an export: the numbers a reviewer (or a Python script, or
 * Excel) needs without owning this decoder. The raw bytes above it stay the
 * source of truth — this is a convenience layer, deliberately derived, and it
 * is regenerated on every save rather than edited.
 *
 * Peaks are reported as the firmware wrote them AND as the header's per-axis
 * peaks, because on some firmware those two disagree: peak_mag_g comes from
 * the detection path while peak_axes_g comes from the capture path. Keeping
 * both means a file can be re-interpreted later without re-exporting.
 */
export function buildExportSummary(parsed, review = null) {
  if (!parsed?.records?.length) return null;
  const stats = summarizeExport(parsed);
  const groups = groupBySession(parsed.records);
  const labels = review?.labels || {};

  return {
    counts: {
      records: stats.total - (stats.markers || 0),
      impacts: stats.impacts,
      events: stats.events,
      session_markers: stats.markers || 0,
      invalid: stats.invalid,
      aborted: stats.aborted,
    },
    peak_mag_g: stats.peakMagG,
    record_versions: stats.recVersions,
    sessions: groups.map((g) => {
      const win = sessionWindow(g);
      const cov = win ? coverage(g, win) : null;
      return {
        session_id: g.sessionId,
        is_epoch: !!g.recordedIsEpoch || !!g.sessionStartIsEpoch,
        started_utc: iso(g.recordedStartMs ?? g.sessionStartMs),
        ended_utc: iso(g.recordedEndMs),
        cut_short: !!g.cutShort,
        duration_ms: win ? win.spanMs : null,
        // Impacts and events are stored as short windows around a detection,
        // so a multi-hour session holds seconds of samples. Stating it here
        // stops a later reader treating the gaps as measured quiet.
        recorded_ms: cov ? cov.recordedMs : null,
        intervals: (g.intervals || []).map((iv) => ({
          started_utc: iso(iv.startMs),
          ended_utc: iso(iv.endMs),
        })),
        impacts: g.impactCount,
        events: g.eventCount,
      };
    }),
    records: parsed.records.map((r) => ({
      frame: r.frameIndex,
      session_id: r.sessionId,
      impact_id: r.impactId,
      kind: r.isMarker ? r.markerKind : r.band,
      rec_type: r.recTypeLabel ?? null,
      trigger_band: r.triggerBandLabel ?? null,
      time_utc: r.timestampIsEpoch ? iso(r.timestampMs) : null,
      uptime_ms: r.timestampIsEpoch ? null : r.timestampMs,
      peak_mag_g: r.peakMagG,
      peak_axes_g: { x: r.peakXG, y: r.peakYG, z: r.peakZG },
      rise_ms: r.riseMs,
      duration_ms: r.durationMs,
      over_count: r.overCount,
      size: r.sizeLabel ?? null,
      // dry-run pulse-width verdict (v8+); "unknown" on older records
      gate: r.gateFlagsLabel ?? null,
      // Peak rotation over the event (v9+). null distinguishes "no gyro in
      // this record" from a measured zero.
      peak_gyro_dps: r.gyroValid ? r.peakGyroMdps / 1000 : null,
      // Per-event feature vector (v10+), decoded. null on older records.
      features: featureDigest(r.features),
      // What a human decided this actually was, if the session has been
      // reviewed against video. This is the label everything else is
      // evidence for, so it travels with the numbers rather than in a
      // separate file that can be lost.
      video_verdict: labels[recordKey(r)]?.label ?? null,
      video_note: labels[recordKey(r)]?.note ?? null,
      samples: r.sampleCount,
      lowg_hz: r.lowgHz,
      highg_hz: r.highgHz,
      valid: r.isValid,
    })),
    // Headers the device never fired on. They have no record by definition,
    // so without them a file can only ever measure precision, never recall.
    missed_headers: (review?.missed || []).map((m) => ({
      session_ms: m.sessionMs,
      note: m.note || null,
    })),
  };
}

/** The v10 feature vector in physical units, so a reader needs neither the
 *  byte layout nor the dB encoding. Band power is emitted in dB with null for
 *  "not computed" — 0 doubles as that in the stored byte and would otherwise
 *  average into a real distribution as a very quiet band. */
function featureDigest(f) {
  if (!f) return null;
  return {
    pre_min_g: f.preMinMg / 1000,
    pre_min_ms_before: f.preMinMsBefore,
    gait_crossings: f.gaitCrossings,
    post_peak_g: f.postPeakMg / 1000,
    post_peak_ms_after: f.postPeakMg ? f.postPeakMsAfter : null,
    decay_ms: f.decayMs,
    pre_window_complete: f.preWindowComplete,
    post_window_complete: f.postWindowComplete,
    band_hz: FEAT_BAND_HZ,
    band_axes: FEAT_AXES,
    bands_db: f.bands.map((row) =>
      row.map((v) => (v ? Number(featBandDb(v).toFixed(1)) : null))
    ),
  };
}

/**
 * Serialize a saved-export file. `raw` is the main-process export result
 * ({ banner, records:[{ index,id,sess,len,byteCount,crc,crcOk,bytesB64 }],
 * aborted, done }) — embedded verbatim.
 *
 * v2 adds the context a dump cannot reconstruct from itself: the device's
 * `status` at the moment of export (above all the thresholds that produced
 * these detections — a v1 file cannot say what 10 g meant), the clock
 * comparison, and a decoded digest. All optional: a file saved without them
 * is still valid, just thinner.
 */
export function buildExportFile({
  wearable,
  mac,
  raw,
  exportedAt,
  status = null,
  clock = null,
  parsed = null,
  review = null,
}) {
  const payload = {
    format: EXPORT_FILE_FORMAT,
    version: EXPORT_FILE_VERSION,
    exported_at: exportedAt || null,
    wearable: {
      field_label: wearable?.field_label ?? null,
      field_label_display: wearable?.field_label_display ?? null,
      serial: wearable?.serial ?? null,
      ble_mac: wearable?.ble_mac ?? mac ?? raw?.banner?.mac ?? null,
    },
    firmware: raw?.banner?.fw ?? null,
    // The device's own `status` object, verbatim. Captured at EXPORT time,
    // not save time: priming or re-configuring a unit between the dump and
    // the save must not rewrite history.
    device: status ? { status } : null,
    // Device clock vs this machine's, so drift is measurable after the fact
    // instead of being silently baked into every timestamp.
    clock,
    summary: buildExportSummary(parsed, review),
    // v3: the video review, whole. The digest above carries each verdict
    // beside its record for a reader that only wants the numbers; this is the
    // review state itself, so reopening the file on any machine restores the
    // labelling instead of starting from an unreviewed session. Verdicts cost
    // an hour of footage each and must not live only in one browser's
    // localStorage.
    review: serializeReview(review),
    raw: {
      banner: raw?.banner ?? null,
      records: raw?.records ?? [],
      aborted: raw?.aborted ?? [],
      done: raw?.done !== false,
    },
  };
  return { payload, json: JSON.stringify(payload) };
}

/** The review, reduced to what is worth persisting. `sync.anchorVideoSec` is
 *  meaningless without the footage it was measured against, so the video's
 *  file name rides along as the only clue about which file that was. */
function serializeReview(review) {
  if (!review) return null;
  const labels = review.labels || {};
  const missed = review.missed || [];
  // An untouched review is noise in the file, not information.
  if (
    !Object.keys(labels).length &&
    !missed.length &&
    !review.sync &&
    !review.offsetMs
  ) {
    return null;
  }
  return {
    sync: review.sync ?? null,
    video_name: review.videoName ?? null,
    // This unit's trim onto the shared clock, so a multi-unit review reopens
    // aligned instead of needing every device lined up again.
    offset_ms: review.offsetMs || 0,
    labels,
    missed,
  };
}

/** The inverse of serializeReview, back into the shape the UI holds. */
export function reviewFromFile(obj) {
  if (!obj) return null;
  return {
    ...emptyReview(),
    sync: obj.sync ?? null,
    videoName: obj.video_name ?? null,
    offsetMs: Number.isFinite(obj.offset_ms) ? obj.offset_ms : 0,
    labels: obj.labels && typeof obj.labels === "object" ? obj.labels : {},
    missed: Array.isArray(obj.missed) ? obj.missed : [],
  };
}

/**
 * Device/host clock comparison at a single instant. `timeEv` is the device's
 * `{"t":"time"}` reply; `hostMs` is Date.now() taken alongside it.
 */
export function buildClockRecord(timeEv, hostMs = Date.now()) {
  if (!timeEv) return null;
  const deviceMs =
    timeEv.epoch_s != null ? timeEv.epoch_s * 1000 + (timeEv.ms || 0) : null;
  return {
    device_synced: timeEv.set === true,
    device_utc: timeEv.utc ?? null,
    device_epoch_ms: deviceMs,
    device_uptime_ms: timeEv.uptime_ms ?? null,
    host_utc: iso(hostMs),
    // Positive = the device clock is AHEAD of this machine.
    offset_ms: deviceMs != null ? deviceMs - hostMs : null,
  };
}

/**
 * Parse + validate a saved-export file. Returns { raw, wearable, firmware,
 * exportedAt }. Throws with a human message on anything unusable.
 */
export function parseExportFile(contents) {
  let obj;
  try {
    obj = JSON.parse(String(contents ?? ""));
  } catch {
    throw new Error(t("utils.exportFile.invalidJson"));
  }
  if (obj?.format !== EXPORT_FILE_FORMAT) {
    throw new Error(t("utils.exportFile.notExportFile"));
  }
  if (
    !(
      obj.version >= EXPORT_FILE_MIN_VERSION &&
      obj.version <= EXPORT_FILE_VERSION
    )
  ) {
    throw new Error(
      t("utils.exportFile.unsupportedVersion", { version: obj.version })
    );
  }
  const records = obj.raw?.records;
  if (!Array.isArray(records) || records.length === 0) {
    throw new Error(t("utils.exportFile.noRecords"));
  }
  if (!records.every((r) => typeof r?.bytesB64 === "string")) {
    throw new Error(t("utils.exportFile.missingRecordData"));
  }
  return {
    raw: obj.raw,
    wearable: obj.wearable || null,
    firmware: obj.firmware ?? null,
    exportedAt: obj.exported_at ?? null,
    // v2 context; null on a v1 file, which the viewer renders as "not
    // captured" rather than pretending the device was in a known state.
    fileVersion: obj.version,
    status: obj.device?.status ?? null,
    clock: obj.clock ?? null,
    summary: obj.summary ?? null,
    // v3 context; null on older files, which simply open unreviewed.
    review: reviewFromFile(obj.review),
  };
}

/**
 * "Export - Unit 002 - 2026-08-17 19.46.json". Minutes included because the
 * fleet workflow saves two sessions per unit, often on the same day.
 */
export function exportFileName({ wearable, mac, date = new Date() }) {
  const who = wearableFileShortName(wearable, mac);
  const pad = (n) => String(n).padStart(2, "0");
  const stamp = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}.${pad(date.getMinutes())}`;
  return `Export - ${who} - ${stamp}.json`;
}
